/**
 * Feature Gating System for Plan Tiering
 *
 * Provides utilities to check feature access based on user's tier
 * and render UI components with appropriate restrictions.
 */

import { getAccountContext } from '../store/api'
import { showUpgradePrompt } from './upgrade-modal'

/**
 * Feature identifiers
 */
export type FeatureId
  = | 'dashboard'
    | 'upload'
    | 'basicAnalysis'
    | 'advancedAnalysis'
    | 'aiInsights'
    | 'memberInvites'
    | 'branchComparison'
    | 'pdfExport'
    | 'apiAccess'
    | 'multipleBranches'

/**
 * Plan type identifiers
 */
export type PlanType = 'free' | 'standard' | 'enterprise'

/**
 * T024: TIER_FEATURES configuration map
 *
 * Maps each tier to its available features
 */
export const TIER_FEATURES: Record<PlanType, FeatureId[]> = {
  free: ['dashboard', 'upload', 'basicAnalysis'],
  standard: [
    'dashboard',
    'upload',
    'basicAnalysis',
    'advancedAnalysis',
    'aiInsights',
    'memberInvites',
  ],
  enterprise: [
    'dashboard',
    'upload',
    'basicAnalysis',
    'advancedAnalysis',
    'aiInsights',
    'memberInvites',
    'branchComparison',
    'pdfExport',
    'apiAccess',
    'multipleBranches',
  ],
}

/**
 * T025: Check feature access utility
 *
 * Checks if the current user's tier allows access to a feature
 *
 * @param feature - Feature to check access for
 * @returns Object with allowed status and required tier if denied
 */
export function checkFeatureAccess(feature: FeatureId): {
  allowed: boolean
  requiredTier: PlanType | null
} {
  const accountContext = getAccountContext()
  const planType = (accountContext?.planType || 'free') as PlanType

  const allowed = TIER_FEATURES[planType]?.includes(feature) ?? false

  if (allowed) {
    return { allowed: true, requiredTier: null }
  }

  // Find minimum required tier for this feature
  for (const [tier, features] of Object.entries(TIER_FEATURES) as [PlanType, FeatureId[]][]) {
    if (features.includes(feature)) {
      return { allowed: false, requiredTier: tier }
    }
  }

  // Feature not found in any tier, default to enterprise
  return { allowed: false, requiredTier: 'enterprise' }
}

/**
 * T026: Render feature gate component wrapper
 *
 * Wraps a content element with a feature gate that shows a locked overlay
 * if the user's tier is insufficient
 *
 * @param feature - Feature to gate
 * @param content - Content element to wrap
 * @returns Wrapped element with gate if needed, or original content if allowed
 */
export function renderFeatureGate(
  feature: FeatureId,
  content: HTMLElement,
): HTMLElement {
  const { allowed, requiredTier } = checkFeatureAccess(feature)

  if (allowed) {
    return content
  }

  // Create wrapper with locked overlay
  const wrapper = document.createElement('div')
  wrapper.className = 'relative'

  // Add opacity and disable pointer events
  content.classList.add('opacity-50', 'pointer-events-none')
  wrapper.appendChild(content)

  // Create locked badge
  const badge = document.createElement('div')
  badge.className
    = 'absolute top-2 right-2 bg-indigo-600 text-white px-3 py-1 rounded-full text-sm font-medium shadow-lg'
  badge.textContent = `${
    requiredTier?.charAt(0).toUpperCase() + requiredTier?.slice(1)
  } Required`
  wrapper.appendChild(badge)

  // Add click handler to show upgrade prompt
  wrapper.addEventListener('click', (e) => {
    e.stopPropagation()
    e.preventDefault()
    if (requiredTier) {
      showUpgradePrompt(feature, requiredTier)
    }
  })

  // Make wrapper clickable
  wrapper.style.cursor = 'pointer'

  return wrapper
}

/**
 * Helper: Get feature display name
 */
export function getFeatureDisplayName(feature: FeatureId): string {
  const names: Record<FeatureId, string> = {
    dashboard: 'Dashboard',
    upload: 'Data Upload',
    basicAnalysis: 'Basic Analysis',
    advancedAnalysis: 'Advanced Analysis',
    aiInsights: 'AI Insights',
    memberInvites: 'Member Invitations',
    branchComparison: 'Branch Comparison',
    pdfExport: 'PDF Export',
    apiAccess: 'API Access',
    multipleBranches: 'Multiple Branches',
  }
  return names[feature] || feature
}

/**
 * Helper: Get tier display name
 */
export function getTierDisplayName(tier: PlanType): string {
  return tier.charAt(0).toUpperCase() + tier.slice(1)
}
